import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Order {
  id: string
  order_id: string
  vendor_name: string
  event_type: string
  event_name: string
  order_date: string
  delivery_time: string
  location: string
  headcount: number
  total_amount: number
  status: string
}

interface Vendor {
  id: string
  name: string
  vendor_type: string
  cuisine: string
  rating: number
  is_preferred: boolean
  status: string
}

export default function CateringPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [ordRes, venRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/catering/orders'),
      fetch(API_BASE + '/catering/vendors'),
      fetch(API_BASE + '/catering/statistics')
    ])
    if (ordRes.ok) setOrders((await ordRes.json()).orders || [])
    if (venRes.ok) setVendors((await venRes.json()).vendors || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': case 'confirmed': case 'active': return 'bg-green-100 text-green-800'
      case 'preparing': case 'submitted': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': case 'inactive': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Catering Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Order</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Vendors</div>
              <div className="text-2xl font-bold">{statistics.active_vendors}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Orders MTD</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_orders_mtd}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Orders</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_orders}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Spent MTD</div>
              <div className="text-2xl font-bold text-green-600">${statistics.total_spent_mtd?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Menu Items</div>
              <div className="text-2xl font-bold">{statistics.menu_items_available}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Catering Orders</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.slice(0, 12).map(ord => (
                  <tr key={ord.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{ord.event_name}</div>
                      <div className="text-xs text-gray-500">{ord.order_id} - {ord.event_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{ord.vendor_name}</div>
                      <div className="text-xs text-gray-500">{ord.order_date} at {ord.delivery_time}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">${ord.total_amount?.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{ord.headcount} people</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ord.status)}`}>{ord.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Vendors</h3>
            <div className="space-y-3">
              {vendors.filter(v => v.status === 'active').slice(0, 10).map(vendor => (
                <div key={vendor.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{vendor.name}</span>
                    {vendor.is_preferred && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Preferred</span>}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{vendor.vendor_type} - {vendor.cuisine}</div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-yellow-500">{'*'.repeat(Math.round(vendor.rating))}</span>
                    <span className="text-xs text-gray-500">{vendor.rating.toFixed(1)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
